import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Users, Activity, AlertTriangle, Clock, XCircle, Send, RefreshCw } from "lucide-react";
import { useLocation } from "wouter";
import { formatDistanceToNow } from "date-fns";

type Tier = "all" | "active" | "at-risk" | "lapsed" | "churned";

type Student = {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  lastSessionDate: string | null;
  daysSince: number;
  totalSessions: number;
  tier: string;
};

type EngagementData = {
  students: Student[];
  summary: { active: number; atRisk: number; lapsed: number; churned: number };
};

const TIER_CONFIG = {
  active: { label: "Active", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", icon: Activity, dot: "bg-emerald-400" },
  "at-risk": { label: "At Risk", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", icon: Clock, dot: "bg-amber-400" },
  lapsed: { label: "Lapsed", color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/20", icon: AlertTriangle, dot: "bg-orange-400" },
  churned: { label: "Churned", color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", icon: XCircle, dot: "bg-red-400" },
} as const;

export default function AdminEngagement() {
  const { user } = useAuth();
  const [, setNav] = useLocation();
  const gymId = user?.gymId ?? "";
  const [activeTier, setActiveTier] = useState<Tier>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch, isFetching } = useQuery<EngagementData>({
    queryKey: ["admin-engagement", gymId],
    queryFn: () => apiFetch(`/admin/analytics/${gymId}/student-engagement`),
    enabled: !!gymId,
  });

  const allStudents = data?.students ?? [];
  const summary = data?.summary ?? { active: 0, atRisk: 0, lapsed: 0, churned: 0 };

  const filtered = activeTier === "all"
    ? allStudents
    : allStudents.filter(s => s.tier === activeTier);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (filtered.every(s => selected.has(s.studentId))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(s => s.studentId)));
    }
  };

  const handleSendMessage = () => {
    const ids = Array.from(selected).join(",");
    setNav(`/admin/messaging?recipients=${ids}&role=STUDENT`);
  };

  const kpiCards = [
    { tier: "active" as const, count: summary.active, label: "Active", sublabel: "< 30 days" },
    { tier: "at-risk" as const, count: summary.atRisk, label: "At Risk", sublabel: "30–60 days" },
    { tier: "lapsed" as const, count: summary.lapsed, label: "Lapsed", sublabel: "60–90 days" },
    { tier: "churned" as const, count: summary.churned, label: "Churned", sublabel: "90+ days" },
  ];

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Student Engagement</h1>
            <p className="text-muted-foreground text-lg">Identify at-risk students and re-engage them before they churn.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} className="gap-2">
            <RefreshCw className={cn("w-3.5 h-3.5", isFetching && "animate-spin")} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpiCards.map(({ tier, count, label, sublabel }) => {
            const cfg = TIER_CONFIG[tier];
            const Icon = cfg.icon;
            const isActive = activeTier === tier;
            return (
              <button
                key={tier}
                onClick={() => setActiveTier(prev => prev === tier ? "all" : tier)}
                className={cn(
                  "text-left p-4 rounded-2xl border transition-all",
                  isActive ? cfg.bg : "glass-panel border-white/5 hover:border-white/10"
                )}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={cn("w-4 h-4", cfg.color)} />
                  <span className={cn("text-xs font-semibold uppercase tracking-wider", cfg.color)}>{label}</span>
                </div>
                <p className="text-3xl font-display font-bold text-white">{isLoading ? "—" : count}</p>
                <p className="text-xs text-muted-foreground mt-1">{sublabel} since last session</p>
              </button>
            );
          })}
        </div>

        <Card className="glass-panel border-white/5">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <CardTitle className="font-display text-lg uppercase tracking-wide text-white">
                  {activeTier === "all" ? "All Students" : TIER_CONFIG[activeTier as keyof typeof TIER_CONFIG]?.label}
                  <span className="ml-2 text-sm text-muted-foreground font-normal normal-case tracking-normal">
                    ({filtered.length})
                  </span>
                </CardTitle>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {(["all", "active", "at-risk", "lapsed", "churned"] as Tier[]).map(t => (
                  <button
                    key={t}
                    onClick={() => { setActiveTier(t); setSelected(new Set()); }}
                    className={cn(
                      "px-3 py-1 rounded-full text-xs font-semibold transition-all border",
                      activeTier === t
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-white/10 text-muted-foreground hover:text-white hover:border-white/20"
                    )}
                  >
                    {t === "all" ? "All" : t === "at-risk" ? "At Risk" : t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
                {selected.size > 0 && (
                  <Button size="sm" onClick={handleSendMessage} className="gap-1.5">
                    <Send className="w-3.5 h-3.5" />
                    Message ({selected.size})
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-14 rounded-xl bg-white/[0.03] animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                <Users className="w-10 h-10 text-muted-foreground" />
                <p className="text-muted-foreground">No students in this category yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left pb-3 pr-4 w-8">
                        <Checkbox
                          checked={filtered.length > 0 && filtered.every(s => selected.has(s.studentId))}
                          onCheckedChange={toggleAll}
                        />
                      </th>
                      <th className="text-left pb-3 pr-4 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Student</th>
                      <th className="text-left pb-3 pr-4 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Status</th>
                      <th className="text-left pb-3 pr-4 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Last Session</th>
                      <th className="text-left pb-3 pr-4 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Days Since</th>
                      <th className="text-left pb-3 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Total Sessions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filtered.map(s => {
                      const cfg = TIER_CONFIG[s.tier as keyof typeof TIER_CONFIG];
                      const isChecked = selected.has(s.studentId);
                      return (
                        <tr
                          key={s.studentId}
                          className={cn("transition-colors", isChecked ? "bg-primary/5" : "hover:bg-white/[0.02]")}
                        >
                          <td className="py-3 pr-4">
                            <Checkbox checked={isChecked} onCheckedChange={() => toggleSelect(s.studentId)} />
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold border border-white/10 shrink-0">
                                {s.firstName[0]}{s.lastName[0]}
                              </div>
                              <div>
                                <p className="text-sm text-white font-medium">{s.firstName} {s.lastName}</p>
                                <p className="text-xs text-muted-foreground">{s.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            {cfg && (
                              <Badge className={cn("text-[10px] font-semibold uppercase tracking-wider border", cfg.bg, cfg.color)}>
                                <span className={cn("w-1.5 h-1.5 rounded-full mr-1.5 inline-block", cfg.dot)} />
                                {cfg.label}
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-sm text-muted-foreground">
                            {s.lastSessionDate
                              ? formatDistanceToNow(new Date(s.lastSessionDate), { addSuffix: true })
                              : "—"}
                          </td>
                          <td className="py-3 pr-4">
                            <span className={cn("font-mono font-bold text-sm", cfg?.color ?? "text-muted-foreground")}>
                              {s.daysSince >= 9999 ? "—" : `${s.daysSince}d`}
                            </span>
                          </td>
                          <td className="py-3">
                            <span className="font-mono text-sm text-white">{s.totalSessions}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
